import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * EditActivityModal — shared modal for editing or deleting an activity row.
 * Used by ContactRecord, DealRecord, and CompanyRecord activity feeds.
 *
 * Props:
 *   activity: the activity object (or null to render nothing)
 *   onSave:   async ({ title, notes }) => boolean — caller persists, returns ok
 *   onDelete: async () => boolean — caller persists deletion, returns ok
 *   onClose:  () => void — close the modal without saving
 *
 * Lifecycle entries (type === "lifecycle") render in read-only mode: title
 * and notes display only, no Save button. Delete is still available so a
 * mistakenly-fired stage move can be cleaned up. Other types are fully
 * editable.
 */
export function EditActivityModal({ activity, onSave, onDelete, onClose }) {
  const [title, setTitle] = useState(activity?.title || "");
  const [notes, setNotes] = useState(activity?.notes || "");
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reset fields whenever a different activity is opened.
  useEffect(() => {
    setTitle(activity?.title || "");
    setNotes(activity?.notes || "");
    setConfirmingDelete(false);
  }, [activity?.id]);

  // Esc closes (unless mid-confirmation, where Esc dismisses the confirm).
  useEffect(() => {
    if (!activity) return;
    const handler = (e) => {
      if (e.key !== "Escape") return;
      if (confirmingDelete) setConfirmingDelete(false);
      else onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activity, confirmingDelete, onClose]);

  if (!activity) return null;

  // Lifecycle entries are read-only (delete-only — preserves audit trail
  // semantics but allows cleanup of mistaken stage moves etc).
  const isReadOnly = activity.type === "lifecycle";

  const handleSave = async () => {
    if (isReadOnly || !title.trim() || saving) return;
    setSaving(true);
    try {
      const ok = await onSave({ title: title.trim(), notes });
      if (ok) onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const ok = await onDelete?.();
      if (ok) onClose?.();
    } finally {
      setDeleting(false);
    }
  };

  return ReactDOM.createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#2C2E33", borderRadius: 12, padding: 24, width: 480, maxWidth: "90vw", border: "1px solid rgba(250,250,250,0.08)" }}>
        <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>
          {isReadOnly ? `Delete ${activity.type}?` : `Edit ${activity.type}`}
        </div>
        <div style={{ fontSize: 12, color: "#a0a3ab", marginBottom: 16 }}>
          {isReadOnly
            ? "Lifecycle entries can't be edited (they preserve the audit log) but can be deleted if created by mistake."
            : "Update the title and notes for this activity."}
        </div>

        <div style={{ fontSize: 11, color: "#a0a3ab", marginBottom: 6 }}>Title</div>
        <input value={title} onChange={e => setTitle(e.target.value)}
          disabled={isReadOnly}
          autoFocus={!isReadOnly}
          style={{
            width: "100%", padding: "8px 10px", borderRadius: 6,
            border: "1px solid rgba(250,250,250,0.1)",
            background: isReadOnly ? "rgba(250,250,250,0.02)" : "rgba(250,250,250,0.04)",
            color: isReadOnly ? "#a0a3ab" : "#FAFAFA",
            fontSize: 13, fontFamily: "inherit", outline: "none",
            boxSizing: "border-box", marginBottom: 14,
            cursor: isReadOnly ? "not-allowed" : "text",
          }} />

        <div style={{ fontSize: 11, color: "#a0a3ab", marginBottom: 6 }}>Notes</div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={8}
          disabled={isReadOnly}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 6,
            border: "1px solid rgba(250,250,250,0.1)",
            background: isReadOnly ? "rgba(250,250,250,0.02)" : "rgba(250,250,250,0.04)",
            color: isReadOnly ? "#a0a3ab" : "#FAFAFA",
            fontSize: 13, fontFamily: "inherit", resize: "vertical",
            boxSizing: "border-box", outline: "none", minHeight: 120,
            cursor: isReadOnly ? "not-allowed" : "text",
          }} />

        {/* Inline delete confirmation — replaces the action row when active */}
        {confirmingDelete ? (
          <div style={{
            marginTop: 14, padding: "12px 14px",
            background: "rgba(220,53,69,0.08)",
            border: "1px solid rgba(220,53,69,0.3)",
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 12, color: "#FAFAFA", marginBottom: 8 }}>
              Are you sure? This can't be undone.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmingDelete(false)} disabled={deleting}
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(250,250,250,0.1)", background: "transparent", color: "#a0a3ab", fontSize: 11, cursor: deleting ? "default" : "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: "#dc3545", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: deleting ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {deleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
            <button onClick={() => setConfirmingDelete(true)} disabled={saving}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(220,53,69,0.3)", background: "transparent", color: "#dc3545", fontSize: 12, fontWeight: 400, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
              Delete
            </button>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} disabled={saving}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(250,250,250,0.1)", background: "transparent", color: "#a0a3ab", fontSize: 12, fontWeight: 400, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              {!isReadOnly && (
                <button onClick={handleSave} disabled={!title.trim() || saving}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: title.trim() && !saving ? "#4059AD" : "#535C6E", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: title.trim() && !saving ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
